import { FormGroup } from '@ksyos/design-system';
import { CustomControlMode, ServiceCode } from '@common/enums';
import { PatientInsuranceOldCarepath } from '@frontend/components/pages/episodeCategory/customControls/patientInsurance/PatientInsuranceOldCarepath';
import type { ICustomControlRequestReadProps } from '../../interfaces/customControlRequestReadProps';
import { getServiceLabel } from '../../../../../../common/customControls/getServiceLabel';
import { ProcessEntity } from '../../../../../../common/enums/processEntity';
import { ServiceType } from '../../../../../../common/enums/serviceType';
import { getDisplayName } from '../../../../../../common/utils/getDisplayName';
import { tooRequestCustomControlDataRedenVanVerwijzingLabels } from '../../../../../../common/validation/customControls/ophthalmology';
import { translateKey } from '../../../../../translate';
import { RequestReadButtons } from '../../../../elements/carePathButtons/RequestReadButtons';
import { FormParagraph } from '../../../../elements/common';
import { FormLabel } from '../../../../elements/forms/FormLabel';
import { HorizontalFormGroupRead } from '../../../../elements/forms/horizontalFormGroupRead/HorizontalFormGroupRead';
import { InputContainer } from '../../../../elements/forms/InputContainer';
import { ProcessContainer, RequestAssetThumbnails } from '../../../episodes/common';
import { ProcessItemFooter } from '../../../episodes/ProcessItemFooter';
import { ProcessItemHeader } from '../../../episodes/ProcessItemHeader';
import { ProfessionalSummaryDataRead } from '../../../professionalSummaryData/ProfessionalSummaryDataRead';
import { HideRequestedExaminationInTaskListAndHomeCheckbox } from '../../../requests/HideRequestedExaminationInTaskListAndHomeCheckbox';
import { CustomControlCollapseBody } from '../../utils/v1/CustomControlCollapseBody';
import { MedicationsRead } from '../../utils/v1/medications/MedicationsRead';
import { ProfileImages } from '../../utils/v1/ProfileImages';
import { TxcsMedicationsList } from '../../utils/v1/TxcsMedicationsList';
import { TooV1FollowingSteps } from './TooV1FollowingSteps';

export function TooV1RequestRead(props: ICustomControlRequestReadProps) {
    return (
        <ProcessContainer isActiveActivity={props.itemState.initialIsOpen}>
            <ProcessItemHeader
                date={props.process.dateSent || (props.request && props.request.dateSent)}
                isFirstItem={props.isFirstItem}
                isFirstProcess={props.itemState.isFirstProcess}
                isItemOpen={props.itemState.isOpen}
                onToggle={async (isOpened) =>
                    await props.updateItemState({
                        ...props.itemState,
                        isOpen: isOpened || props.itemState.initialIsOpen === true,
                    })
                }
                organizationName={props.process.requesterOrganization?.name}
                practitionerName={
                    props.process.requesterPractitioner
                        ? getDisplayName(props.process.requesterPractitioner)
                        : undefined
                }
                process={props.process}
                serviceType={ServiceType.inquiry}
                title={getServiceLabel(props.process.code, 'request', props.process.name)}
            />

            <PatientInsuranceOldCarepath
                isPatientInformed={props.request.customControlData.isPatientInformed}
                mode={CustomControlMode.read}
                serviceCode={ServiceCode.TOO}
            />

            <ProfileImages request={props.request} />

            <CustomControlCollapseBody isOpen={props.itemState.isOpen}>
                {props.request.customControlData.patientEmailAddress && (
                    <HorizontalFormGroupRead>
                        <FormLabel>{translateKey('patient.patientEmailAddress')}</FormLabel>
                        <InputContainer>
                            <FormParagraph bold>{props.request.customControlData.patientEmailAddress}</FormParagraph>
                        </InputContainer>
                    </HorizontalFormGroupRead>
                )}

                <HorizontalFormGroupRead>
                    <FormLabel>Reden van verwijzing</FormLabel>
                    <InputContainer>
                        {getRedenVanVerwijzing().map((label) => {
                            return (
                                <FormParagraph
                                    bold
                                    key={label}
                                >
                                    {label}
                                </FormParagraph>
                            );
                        })}
                    </InputContainer>
                </HorizontalFormGroupRead>

                {(props.request.customControlData.files ?? []).length > 0 && (
                    <FormGroup label={translateKey('request.referralLetter')}>
                        <RequestAssetThumbnails
                            assets={props.request.customControlData.files}
                            requestId={props.request.id}
                        />
                    </FormGroup>
                )}

                {props.request.customControlData.omschrijf_de_duur_van_de_klachten && (
                    <HorizontalFormGroupRead>
                        <FormLabel>Duur van de klachten</FormLabel>
                        <InputContainer>
                            <FormParagraph bold>
                                {props.request.customControlData.omschrijf_de_duur_van_de_klachten}
                            </FormParagraph>
                        </InputContainer>
                    </HorizontalFormGroupRead>
                )}

                {props.request.customControlData.relevante_medische_voorgeschiedenis && (
                    <HorizontalFormGroupRead>
                        <FormLabel>Medische voorgeschiedenis</FormLabel>
                        <InputContainer>
                            <FormParagraph bold>
                                {props.request.customControlData.relevante_medische_voorgeschiedenis}
                            </FormParagraph>
                        </InputContainer>
                    </HorizontalFormGroupRead>
                )}
                {props.request.customControlData.oculaire_voorgeschiedenis && (
                    <HorizontalFormGroupRead>
                        <FormLabel>Oculaire voorgeschiedenis</FormLabel>
                        <InputContainer>
                            <FormParagraph bold>
                                {props.request.customControlData.oculaire_voorgeschiedenis}
                            </FormParagraph>
                        </InputContainer>
                    </HorizontalFormGroupRead>
                )}
                {props.request.customControlData.familiaire_voorgeschiedenis && (
                    <HorizontalFormGroupRead>
                        <FormLabel>Familiaire voorgeschiedenis</FormLabel>
                        <InputContainer>
                            <FormParagraph bold>
                                {props.request.customControlData.familiaire_voorgeschiedenis}
                            </FormParagraph>
                        </InputContainer>
                    </HorizontalFormGroupRead>
                )}
                {props.request.customControlData.toelichting && (
                    <HorizontalFormGroupRead>
                        <FormLabel>Toelichting</FormLabel>
                        <InputContainer>
                            <FormParagraph bold>{props.request.customControlData.toelichting}</FormParagraph>
                        </InputContainer>
                    </HorizontalFormGroupRead>
                )}
                <MedicationsRead
                    entity={ProcessEntity.request}
                    entityId={props.request.id}
                    patientUsesMedication={props.request.patientUsesMedication}
                />
                <ProfessionalSummaryDataRead request={props.request} />
                <TxcsMedicationsList requestId={props.request.id} />

                <TooV1FollowingSteps />

                <RequestReadButtons {...props} />
                <HideRequestedExaminationInTaskListAndHomeCheckbox
                    isLastItem={props.isLastItem}
                    reload={props.reloadRequest}
                    request={props.request}
                />
            </CustomControlCollapseBody>

            {!props.itemState.initialIsOpen && (
                <ProcessItemFooter
                    isItemOpen={props.itemState.isOpen}
                    labelLess={translateKey('request.hideRequest')}
                    labelMore={translateKey('request.showRequest')}
                    onToggle={async (isOpened) => await props.updateItemState({ ...props.itemState, isOpen: isOpened })}
                />
            )}
        </ProcessContainer>
    );

    function getRedenVanVerwijzing() {
        const checkedRedenVanVerwijzing: string[] = [];

        Object.keys(props.request.customControlData.reden_van_verwijzing).forEach((key) => {
            if (props.request.customControlData.reden_van_verwijzing[key] === true) {
                checkedRedenVanVerwijzing.push(tooRequestCustomControlDataRedenVanVerwijzingLabels[key]);
            } else if (typeof props.request.customControlData.reden_van_verwijzing[key] === 'string') {
                checkedRedenVanVerwijzing.push(props.request.customControlData.reden_van_verwijzing[key]);
            }
        });

        return checkedRedenVanVerwijzing;
    }
}
